import {
  ForecastMapConfig,
  ForecastMapCountry,
  ForecastMapRegion,
} from '../../api/types';

// Re-exported alongside the grouping so consumers (the browse screens key
// their headings on these zone enums) get them from one place.
export type {ForecastMapCountry, ForecastMapRegion};

export type ZoneCountryGroup = {
  // null = region-level configs (e.g. the synoptic maps), rendered directly
  // under the region with no country sub-heading.
  country: ForecastMapCountry | null;
  configs: ForecastMapConfig[];
};

export type ZoneRegionGroup = {
  region: ForecastMapRegion;
  countries: ZoneCountryGroup[];
};

// Stable region order for the browse; regions absent from the catalog are
// skipped, and any future region not listed here is appended in catalog order.
const REGION_ORDER: ForecastMapRegion[] = ['NORTH_AMERICA', 'EUROPE'];

/**
 * The "by zone" browse (#50): the zone-tagged (`region != null`) configs
 * grouped by region then country/area. Region-level configs (country == null)
 * come first within their region; per-country groups follow in catalog order.
 * Configs with no region are not surfaced here.
 */
export const groupByZone = (
  configs: ForecastMapConfig[],
): ZoneRegionGroup[] => {
  const byRegion = new Map<ForecastMapRegion, ForecastMapConfig[]>();
  for (const config of configs) {
    if (config.region === null) {
      continue;
    }
    const existing = byRegion.get(config.region);
    if (existing === undefined) {
      byRegion.set(config.region, [config]);
    } else {
      existing.push(config);
    }
  }

  const orderedRegions: ForecastMapRegion[] = [
    ...REGION_ORDER.filter((region) => byRegion.has(region)),
    ...[...byRegion.keys()].filter((region) => !REGION_ORDER.includes(region)),
  ];

  return orderedRegions.map((region) => {
    const regionConfigs = byRegion.get(region) ?? [];
    const regionLevel: ForecastMapConfig[] = [];
    const byCountry = new Map<ForecastMapCountry, ForecastMapConfig[]>();
    for (const config of regionConfigs) {
      if (config.country === null) {
        regionLevel.push(config);
        continue;
      }
      const existing = byCountry.get(config.country);
      if (existing === undefined) {
        byCountry.set(config.country, [config]);
      } else {
        existing.push(config);
      }
    }
    const countries: ZoneCountryGroup[] = [
      ...(regionLevel.length > 0
        ? [{country: null, configs: regionLevel}]
        : []),
      ...[...byCountry.entries()].map(([country, cfgs]) => ({
        country,
        configs: cfgs,
      })),
    ];
    return {region, countries};
  });
};
